import { Col, Container, Row, Tab } from 'react-bootstrap'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import buttonStyles from '../../components/Button/Button.module.scss'
import React, { useState } from 'react'
import { useGetUsersQuery } from '../../store/user/user'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Tabs } from '../../components/Tabs/Tabs'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { useZocksAuth } from '../../components/ScopedBlock/hooks'
import { PageMetadata } from '../../models/PageMetadata'
import styled from 'styled-components'
import { CrossAccountGrantStatus, useListCrossAccountGrantsOfAccountQuery } from '../../store/crossAccount/crossAccount'
import { CrossAccountMembersTab } from './CrossAccountMembersTab'
import { PendingGrantsTab } from './PendingGrantsTab'
import { GrantCrossAccountAccessCta } from './GrantCrossAccountAccessCta'
import { GrantCrossAccountAccessModal } from './GrantCrossAccountAccessModal'
import { SuccessToast } from '../../components/SuccessToast/SuccessToast'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { TeamSection } from './TeamSection'
import { TEAM_TABS } from './teamTabs'

const CROSS_ACCOUNT_TABS = ['cross-account-members', 'cross-account-pending-grants']

const CustomTabs = styled(Tabs)`
  .nav-link.active {
    background-color: var(--zocks-primary-light) !important;
  }
`

export const ManageTeam = () => {
  useDocumentTitle('Team')
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { activeTab = 'members' } = useParams()
  const selectedTab = CROSS_ACCOUNT_TABS.includes(activeTab) ? 'cross-account' : 'team'
  const { data: users } = useGetUsersQuery({
    accountId: accountId,
    pageRequest: { page: 0, size: 10 },
    identityTypes: ['idp'],
  })
  const { data: grants } = useListCrossAccountGrantsOfAccountQuery({
    accountId: accountId,
    statuses: [CrossAccountGrantStatus.ACTIVE],
    pageRequest: { page: 0, size: 10 },
  })

  const changeTab = (key: string | null) => {
    if (key) {
      const manageTeamTab = key === 'cross-account' ? CROSS_ACCOUNT_TABS[0] : TEAM_TABS[0]
      navigate(`/account/manage-team/${manageTeamTab}`, { replace: true })
    }
  }

  return (
    <Container fluid>
      <Row className='mb-8'>
        <Col className='p-0'>
          <Container>
            <Row>
              <Col xs='auto'>
                <UserAvatar icon='bi bi-people' width='4.5rem' height='4.5rem' className='my-team-user-avatar' fontSize='1.5rem' />
              </Col>
              <Col>
                <Row>
                  <Col>
                    <h3>My Team</h3>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {users && grants ? (
                      <div className='paragraph-medium zocks-text-gray-600'>{users?.page.totalElements + grants?.page.totalElements} members</div>
                    ) : null}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        <Col className='d-none d-md-flex align-items-center ' xs='auto'>
          <NavLink className={`${buttonStyles.button} ${buttonStyles.primaryGhostButton}`} to={'/account/profile-settings'}>
            View my profile
          </NavLink>
        </Col>
      </Row>
      <CustomTabs className='mb-6 nav-fill' defaultActiveKey={selectedTab} onSelect={changeTab} id='manage-team-tabs' activeKey={selectedTab}>
        <Tab eventKey='team' id='team-members' title={users ? `Team Members (${users.page.totalElements})` : 'Team Members'}>
          <TeamSection usersPage={users?.page} />
        </Tab>
        <Tab
          eventKey='cross-account'
          id='cross-account-members'
          mountOnEnter={false}
          title={grants ? `Cross-Account Access (${grants.page.totalElements})` : 'Cross-Account Access'}
        >
          <CrossAccountMembersTabs grantsPage={grants?.page} />
        </Tab>
      </CustomTabs>
    </Container>
  )
}

interface CrossAccountMembersTabsProps {
  grantsPage: PageMetadata | undefined
}

const CrossAccountMembersTabs: React.FC<CrossAccountMembersTabsProps> = ({ grantsPage }) => {
  const { activeTab = null } = useParams()
  const [showGrantAccessModal, setShowGrantAccessModal] = useState<boolean>(activeTab === 'grant')
  const [showToast, setShowToast] = useState(false)
  const selectedTab = activeTab && CROSS_ACCOUNT_TABS.includes(activeTab) ? activeTab : null
  const { hasScope } = useZocksAuth()
  const hasInviteScope = hasScope('**', 'consoleuser:invite', ['consoleuser:admin'])

  const navigate = useNavigate()

  const handleGrantAccessClick = () => {
    setShowGrantAccessModal(true)
    navigate(`/account/manage-team/grant`, { replace: true })
  }

  const handleGrantCrossAccountAccessModalHide = (isSuccess: boolean) => {
    setShowGrantAccessModal(false)
    setShowToast(isSuccess)
    navigate(`/account/manage-team/cross-account-members`, { replace: true })
  }

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(`/account/manage-team/${key}`, { replace: true })
    }
  }

  return (
    <Container className='p-0'>
      <SuccessToast message='Access granted. The user needs to manually accept it for activation.' show={showToast} onClose={() => setShowToast(false)} />
      {showGrantAccessModal ? <GrantCrossAccountAccessModal show={showGrantAccessModal} onHide={handleGrantCrossAccountAccessModalHide} /> : null}
      <Row className='mb-6'>
        <Col>
          {grantsPage ? (
            <ScopedBlock
              scope={{
                scope: 'consoleuser:invite',
                superScope: ['consoleuser:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'auth.crossAccountLoginEnabled.value',
                }}
              >
                <GrantCrossAccountAccessCta onGrantAccess={handleGrantAccessClick} />
              </ScopedBlock>
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs className='mb-4' defaultActiveKey={selectedTab || undefined} onSelect={changeTab} id='manage-team-tabs' activeKey={selectedTab || undefined}>
            <Tab eventKey='cross-account-members' id='cross-account-members' title='Cross-Account'>
              <CrossAccountMembersTab />
            </Tab>
            {hasInviteScope ? (
              <Tab eventKey='cross-account-pending-grants' id='pending-grants' title='Pending'>
                <PendingGrantsTab />
              </Tab>
            ) : null}
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}
